// REACT
import { useState, useEffect, useContext } from "react";

// CONTEXT
import { maincontext } from "../context/maincontext"; // Import the context

// Handles API response processing
const handleResponse = async (response, setData, logout, isDownload = false) => {
  if (!response.ok) {
    if (response.status === 401) {
      logout({ returnTo: window.location.origin });
      return;
    }

    // Try to extract message from response body
    let errorMessage = `Error ${response.status}: ${response.statusText}`;
    try {
      const text = await response.text();
      const maybeJson = JSON.parse(text);
      if (maybeJson.message) {
        errorMessage = maybeJson.message;
      }
    } catch {
      // Ignore JSON parse errors; use default message
    }

    throw new Error(errorMessage);
  }

  if (isDownload) {
    return response.blob(); // Return file blob for downloads
  }

  const contentType = response.headers.get("Content-Type");
  if (contentType && contentType.startsWith("image/")) {
    const blob = await response.blob();
    return URL.createObjectURL(blob);
  } else {
    return response.json();
  }
};

// Handles API requests
const handleRequest = async (method, url, logout, token, body = null, isFormData = false, isDownload = false, isUpload = false) => {
  let options = {
    method,
    credentials: "omit",
    headers: {
      "Authorization": `Bearer ${token}`,
      ...(isFormData || isUpload ? {} : { "Content-Type": "application/json" }), // No Content-Type for FormData
    },
    body: isUpload ? body : isFormData ? body : body ? JSON.stringify(body) : null, // Correctly pass FormData
  };

  const response = await fetch(url, options);
  return handleResponse(response, (data) => data, logout, isDownload);
};

// Generic API hook that now uses context
const useApi = (method, url, body = null, trigger = null, isFormData = false, isDownload = false, isUpload = false) => {
  const { token, logout  } = useContext(maincontext);
  const [data, setData] = useState(null);
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!url || !token) {
      !token && console.log("No token was given!")
      return;
    }
    const methodsRequiringBody = ["POST", "PUT", "PATCH"];
    if (methodsRequiringBody.includes(method) && (!body || (typeof body === "object" && !(body instanceof FormData) && Object.keys(body).length === 0))) {
      return;
    }

    const fetchData = async () => {
      setIsPending(true);
      try {
        const result = await handleRequest(method, url, logout, token, body, isFormData, isDownload, isUpload);
        setData(result);
        setError(null);
      } catch (error) {
        setError(error.message);
      } finally {
        setIsPending(false);
      }
    };

    fetchData();

    // eslint-disable-next-line
  }, [method, url, token, body, trigger]);

  return { data, isPending, error };
};

// CRUD
const useCreate = (datapackage, url) => useApi("POST", url, datapackage);
const useRead = (url, trigger) => useApi("GET", url, null, trigger);
const useUpdate = (datapackage, url) => useApi("PUT", url, datapackage);
const useDelete = (url) => useApi("DELETE", url);

// File download
const useDownload = (datapackage, url, filename = "export.xlsx", method = "POST", trigger = null) => {
  const { data, isPending, error } = useApi(method, url, datapackage, trigger, false, true);

  useEffect(() => {
    if (data && data instanceof Blob) {
      const fileUrl = window.URL.createObjectURL(data);
      const link = document.createElement("a");
      link.href = fileUrl;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(fileUrl);
    }

    // eslint-disable-next-line
  }, [data]);

  return { isPending, error };
};

// Upload
const useUpload = (data, url) => {
  return useApi("POST", url, data, null, true, false, true);
};

export { useCreate, useRead, useUpdate, useDelete, useUpload, useDownload };
